import React from 'react';
import { cn } from './cn';

/**
 * LibTable — the shared list-page table from the prototype.
 *
 * Matches the `.lib-table` selector in cms-redesign-mockup.html. Mostly a
 * thin wrapper around <table> that applies the design tokens (font sizes,
 * border, hover) consistently. Consumers compose with <thead>/<tbody>/<tr>/
 * <td>/<th> directly so the API stays familiar.
 *
 *   <LibTable>
 *     <LibTable.Head>
 *       <tr>
 *         <th>Category</th>
 *         <LibTable.NumTh>Sessions</LibTable.NumTh>
 *         <LibTable.CenterTh>Status</LibTable.CenterTh>
 *       </tr>
 *     </LibTable.Head>
 *     <tbody>…</tbody>
 *   </LibTable>
 */
export function LibTable({ children, className, style, fixed = false }) {
  return (
    <table
      className={cn(
        'w-full border-collapse bg-surface font-sans',
        className,
      )}
      // `fixed` switches to `table-layout: fixed` so column widths honor
      // their declared values instead of expanding to fit content. Required
      // any time a row's content (long titles, author names, parent labels)
      // would otherwise push the table past the viewport.
      style={fixed ? { tableLayout: 'fixed', ...style } : style}
    >
      {children}
    </table>
  );
}

// `.sessions thead th` (prototype 1091–1106) AND `.lib-table th` (1877–1886)
// both use the same recipe:
//   Asap Condensed 11/700 uppercase, letter-spacing 0.08em, text-light,
//   padding ~11/12, sand top + bottom borders, CREAM BACKGROUND.
// Sessions also makes it sticky so the head stays visible during long
// scrolls; that's safe to declare on every LibTable instance — sticky
// only engages when an ancestor has overflow scrolling.
const TH_BASE = cn(
  'text-left font-condensed font-bold uppercase',
  'text-text-light bg-cream border-y border-sand',
  'first:pl-7 last:pr-7',
  'sticky top-0 z-[2]',
);
const TH_STYLE = {
  fontSize: 11,
  letterSpacing: '0.08em',
  padding: '11px 12px',
};

LibTable.Head = function Head({ children, className }) {
  return <thead className={cn(className)}>{children}</thead>;
};

LibTable.Th = function Th({ children, className, style, ...rest }) {
  return (
    <th className={cn(TH_BASE, className)} style={{ ...TH_STYLE, ...style }} {...rest}>
      {children}
    </th>
  );
};

LibTable.NumTh = function NumTh({ children, className, style, ...rest }) {
  return (
    <th
      className={cn(TH_BASE, 'text-right', className)}
      style={{ ...TH_STYLE, ...style }}
      {...rest}
    >
      {children}
    </th>
  );
};

LibTable.CenterTh = function CenterTh({ children, className, style, ...rest }) {
  return (
    <th
      className={cn(TH_BASE, 'text-center', className)}
      style={{ ...TH_STYLE, ...style }}
      {...rest}
    >
      {children}
    </th>
  );
};

// `.sessions tbody tr` (1118–1130): hover bg cream, transition 120ms.
// `.sessions tbody td` (1108–1116): padding 14 12, line border-b, surface bg.
// First/last child get the 28px gutter.
// `forwardRef` because dnd-kit's useSortable needs to attach `setNodeRef`
// to the <tr> for drag-reorder pages (Categories etc.).
LibTable.Row = React.forwardRef(function Row(
  { children, className, inactive, onClick, ...rest },
  ref,
) {
  return (
    <tr
      ref={ref}
      onClick={onClick}
      data-inactive={inactive ? 'true' : undefined}
      className={cn(
        'group transition-colors',
        // Active rows hover cream; inactive rows already sit on cream and
        // bump one notch darker via the `data-[inactive]:hover` style below.
        !inactive && 'hover:bg-cream',
        onClick && 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
});

// Each <td>: padding 14 12, 1px line bottom, surface bg by default, 13.5px
// navy. First and last cells take the 28px page gutter. When the row is
// marked inactive (`data-inactive` on the <tr>), every <td> picks up cream
// background via the [data-inactive] selector below.
const TD_BASE = cn(
  'text-navy align-middle border-b border-line',
  'first:pl-7 last:pr-7',
  'group-data-[inactive=true]:bg-[var(--color-row-inactive)]',
  'group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]',
);
const TD_STYLE = { padding: '14px 12px', fontSize: 13.5 };

LibTable.Td = function Td({ children, className, style, ...rest }) {
  return (
    <td className={cn(TD_BASE, className)} style={{ ...TD_STYLE, ...style }} {...rest}>
      {children}
    </td>
  );
};

LibTable.NumTd = function NumTd({ children, muted, className, style, ...rest }) {
  return (
    <td
      className={cn(
        TD_BASE,
        'text-right font-mono',
        muted ? 'text-text-muted' : 'text-navy font-bold',
        className,
      )}
      style={{ ...TD_STYLE, fontSize: 12.5, ...style }}
      {...rest}
    >
      {children}
    </td>
  );
};

LibTable.CenterTd = function CenterTd({ children, className, style, ...rest }) {
  return (
    <td className={cn(TD_BASE, 'text-center', className)} style={{ ...TD_STYLE, ...style }} {...rest}>
      {children}
    </td>
  );
};

export default LibTable;
